/** Draws resolved evidence and analysis groups. Membership, labels, quotations and recommendations belong to the server. */
import type {
  ShopifyResultQuote,
  ShopifyResultPhrases,
  ShopifyResultClassifications,
  ShopifyResultInquiry,
} from '@testfeed/shopify-contracts'
import { BlockHeading } from '../study-builder/SectionFrame'
import './response-phrases.css'
import { ServerResultQuote } from './ServerResultQuote'
import { ServerEvidencePassage } from './ServerEvidencePassage'
import { ServerResultCodebook } from './ServerResultCodebook'
import { ServerInquiryConversation } from './ServerInquiryConversation'
import { ServerResultPaging } from './ServerQuestionNavigation'
import { NextTestDraftAction } from './NextTestDraftAction'
export { ServerResultQuote } from './ServerResultQuote'

export function ServerResultNotes({ notes }: { notes: string[] }) {
  return (
    <>
      {notes.map((note) => (
        <s-paragraph key={note} color="subdued">
          {note}
        </s-paragraph>
      ))}
    </>
  )
}

export function ServerResultPhrases({ data }: { data: ShopifyResultPhrases }) {
  const codebook = data.codebook ? <ServerResultCodebook data={data.codebook} /> : null
  const otherAnswers = data.otherAnswers.length ? (
    <details className="response-phrase-disclosure">
      <summary>{data.labels.otherAnswers}</summary>
      <s-stack direction="block" gap="large">
        {data.otherAnswers.map((answer) => (
          <ServerResultQuote key={answer.id} answer={answer} />
        ))}
      </s-stack>
    </details>
  ) : null
  return (
    <s-stack direction="block" gap="large-200">
      {codebook}
      <ServerResultNotes notes={data.notes} />
      <PhraseScope data={data} />
      <PhraseActions data={data} />
      <div className="response-phrase-flow">
        {data.observations.map((observation, index) => (
          <div className="response-phrase-step" key={observation.id}>
            <span className="response-phrase-number" aria-hidden="true">
              {index + 1}
            </span>
            <div className="response-phrase-reading">
              <BlockHeading>{observation.title}</BlockHeading>
              <div className="response-phrase-reading">
                <s-stack direction="block" gap="base">
                  <ObservationPassages observation={observation} />
                  <details className="response-phrase-disclosure">
                    <summary>{data.labels.evidence}</summary>
                    <s-stack direction="block" gap="large">
                      <s-paragraph>{observation.interpretation}</s-paragraph>
                      {observation.evidence.map((group, groupIndex) => (
                        <s-stack direction="block" gap="base" key={groupIndex}>
                          <s-text type="strong">{group.title}</s-text>
                          <EmptyEvidence answers={group.answers} text={group.emptyText} />
                          {group.answers.map((answer) => (
                            <ServerResultQuote key={answer.id} answer={answer} />
                          ))}
                        </s-stack>
                      ))}
                    </s-stack>
                  </details>
                </s-stack>
              </div>
              <s-divider />
            </div>
          </div>
        ))}
      </div>
      <PhraseQuestions data={data} />
      {data.proposalGroups.map((group, index) => (
        <s-stack direction="block" gap="large" key={index}>
          <BlockHeading>{group.title}</BlockHeading>
          {group.proposals.map((proposal) => (
            <s-stack direction="block" gap="base" key={proposal.id}>
              <s-text type="strong">{proposal.title}</s-text>
              <s-paragraph>{proposal.hypothesis}</s-paragraph>
              <s-paragraph color="subdued">{proposal.description}</s-paragraph>
              <NextTestDraftAction action={proposal.createDraft} />
              <details className="response-phrase-disclosure">
                <summary>{data.labels.explore}</summary>
                <s-stack direction="block" gap="base">
                  {proposal.details.map((text) => (
                    <s-paragraph key={text}>{text}</s-paragraph>
                  ))}
                  {proposal.evidence.map((answer) => (
                    <ServerResultQuote key={answer.id} answer={answer} />
                  ))}
                </s-stack>
              </details>
            </s-stack>
          ))}
        </s-stack>
      ))}
      <ServerResultPaging data={data.navigation} />
      {otherAnswers}
    </s-stack>
  )
}

function PhraseScope({ data }: { data: ShopifyResultPhrases }) {
  if (!data.scope.length) return null
  return (
    <details className="response-phrase-disclosure">
      <summary>{data.scopeHeading}</summary>
      <s-stack direction="block" gap="base">
        {data.scope.map((scope, index) => (
          <s-stack key={index} direction="block" gap="small">
            <s-text type="strong">{scope.label}</s-text>
            <s-text color="subdued">{scope.detail}</s-text>
          </s-stack>
        ))}
      </s-stack>
    </details>
  )
}

function ObservationPassages({ observation }: { observation: ShopifyResultPhrases['observations'][number] }) {
  if (!observation.passages?.length) return <ServerResultQuote answer={observation.answer} />
  return (
    <s-stack direction="block" gap="large">
      <s-paragraph>{observation.interpretation}</s-paragraph>
      {observation.passages.map((passage, index) => (
        <s-stack key={`${passage.answer.id}:${index}`} direction="block" gap="base">
          <ServerEvidencePassage title={passage.title} explanation={passage.explanation} answer={passage.answer} />
          {passage.counts.map((count) => (
            <s-stack key={count.label} direction="block" gap="small">
              <s-text type="strong">{count.label}</s-text>
              <s-text color="subdued">{count.detail}</s-text>
            </s-stack>
          ))}
        </s-stack>
      ))}
    </s-stack>
  )
}

function PhraseActions({ data }: { data: ShopifyResultPhrases }) {
  if (!data.actions.length) return null
  return (
    <s-stack direction="block" gap="large">
      <BlockHeading>{data.actionsHeading}</BlockHeading>
      {data.actions.map((action) => (
        <s-stack key={action.id} direction="block" gap="base">
          <s-text type="strong">{action.suggestion}</s-text>
          <s-text color="subdued">{action.title}</s-text>
          <s-paragraph>{action.reason}</s-paragraph>
          <s-paragraph color="subdued">{action.notice}</s-paragraph>
          <details className="response-phrase-disclosure">
            <summary>{data.labels.evidence}</summary>
            <s-stack direction="block" gap="large">
              {action.evidence.map((group) => (
                <s-stack direction="block" gap="base" key={group.title}>
                  <s-text type="strong">{group.title}</s-text>
                  <EmptyEvidence answers={group.answers} text={group.emptyText} />
                  {group.answers.map((answer) => (
                    <ServerResultQuote key={answer.id} answer={answer} />
                  ))}
                </s-stack>
              ))}
            </s-stack>
          </details>
        </s-stack>
      ))}
    </s-stack>
  )
}

function EmptyEvidence({ answers, text }: { answers: ShopifyResultQuote[]; text: string }) {
  if (answers.length) return null
  return <s-paragraph color="subdued">{text}</s-paragraph>
}

function PhraseQuestions({ data }: { data: ShopifyResultPhrases }) {
  if (!data.questions.length) return null
  return (
    <s-stack direction="block" gap="large">
      <BlockHeading>{data.questionsHeading}</BlockHeading>
      {data.questions.map((question) => (
        <s-stack key={question.id} direction="block" gap="base">
          <s-text type="strong">{question.question}</s-text>
          <s-paragraph>{question.detail}</s-paragraph>
        </s-stack>
      ))}
    </s-stack>
  )
}

type ClassifiedAnswer = ShopifyResultClassifications['review'][number]
function ClassifiedAnswers({ answers }: { answers: ClassifiedAnswer[] }) {
  return (
    <s-stack direction="block" gap="base">
      {answers.map((answer) => (
        <s-stack key={answer.id} direction="block" gap="small">
          <s-text color="subdued">{answer.source}</s-text>
          <s-paragraph>{answer.text}</s-paragraph>
          <ServerResultNotes notes={answer.notes} />
        </s-stack>
      ))}
    </s-stack>
  )
}

export function ServerResultClassifications({ data }: { data: ShopifyResultClassifications }) {
  const sample = data.sample ? (
    <s-stack direction="block" gap="base">
      <BlockHeading>{data.sample.title}</BlockHeading>
      <ServerResultNotes notes={data.sample.notes} />
    </s-stack>
  ) : null
  const review = data.review.length ? (
    <s-stack direction="block" gap="base">
      <BlockHeading>{data.reviewHeading}</BlockHeading>
      <ClassifiedAnswers answers={data.review} />
    </s-stack>
  ) : null
  return (
    <s-stack direction="block" gap="large">
      <ServerResultNotes notes={data.notes} />
      <s-paragraph>{data.objective}</s-paragraph>
      <OptionalText text={data.decision} />
      {sample}
      {data.groups.map((group) => (
        <s-stack key={group.id} direction="block" gap="base">
          <BlockHeading>{group.title}</BlockHeading>
          <s-text color="subdued">{group.role}</s-text>
          <ClassifiedAnswers answers={group.answers} />
        </s-stack>
      ))}
      {review}
    </s-stack>
  )
}

export function ServerResultInquiry({ data }: { data: ShopifyResultInquiry }) {
  return (
    <s-stack direction="block" gap="large">
      <ServerResultNotes notes={data.notes} />
      {(data.conversations ?? []).map((conversation) => (
        <ServerInquiryConversation key={conversation.id} conversation={conversation} />
      ))}
      {(data.proposals ?? []).map((proposal) => (
        <s-stack direction="block" gap="base" key={proposal.id}>
          <s-text color="subdued">{proposal.statusLabel}</s-text>
          <ServerResultQuote answer={proposal.evidence} />
          <s-text type="strong">{proposal.missingLabel}</s-text>
          <s-paragraph>{proposal.missingDistinction}</s-paragraph>
          <s-text type="strong">{proposal.questionLabel}</s-text>
          <s-paragraph>{proposal.question}</s-paragraph>
        </s-stack>
      ))}
      {data.runs.map((run) => (
        <s-stack direction="block" gap="large" key={run.id}>
          <BlockHeading>{run.title}</BlockHeading>
          <s-paragraph>{run.message}</s-paragraph>
          {run.exchanges.map((exchange) => (
            <s-stack direction="block" gap="base" key={exchange.id}>
              <s-text type="strong">{exchange.question}</s-text>
              <s-text color="subdued">{exchange.originalLabel}</s-text>
              <s-paragraph>{exchange.originalAnswer}</s-paragraph>
              {exchange.followups.map((followup, index) => (
                <s-stack direction="block" gap="small-200" key={index}>
                  <s-text color="subdued">{followup.questionLabel}</s-text>
                  <s-paragraph>{followup.question}</s-paragraph>
                  <s-text color="subdued">{followup.answerLabel}</s-text>
                  <s-paragraph>{followup.answer}</s-paragraph>
                  <OptionalText text={followup.warning} />
                </s-stack>
              ))}
              {exchange.findings.map((finding) => (
                <s-stack direction="block" gap="small-200" key={finding.id}>
                  <s-text type="strong">{finding.title}</s-text>
                  <s-paragraph>{finding.description}</s-paragraph>
                </s-stack>
              ))}
            </s-stack>
          ))}
        </s-stack>
      ))}
    </s-stack>
  )
}

export function OptionalText({ text }: { text: string | null }) {
  if (!text) return null
  return <s-text color="subdued">{text}</s-text>
}

export function AnswerImage({ url, label }: { url: string | null; label: string }) {
  if (!url) return null
  return <s-thumbnail src={url} alt={label} size="small" />
}
